import { useState } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { makePostRequest, makePutRequest } from "../../utilities/common";
import { defaultUrl } from "../../features/actions";

export const LinkParentForm = ({ student_id, close }: any) => {
  const MAX_PARENTS = 2;

  const [parents, setParents] = useState([
    {
      name: "",
      email: "",
      phone: "",
      home_number: "",
      work_number: "",
      relationship: "",
    },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // UPDATE FIELD
  const handleChange = (index: number, e: any) => {
    const { name, value } = e.target;
    setParents((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [name]: value } : p))
    );
  };

  // REMOVE ENTRY
  const removeParent = (index: number) => {
    setParents((prev) => prev.filter((_, i) => i !== index));
  };

  // ADD NEW PARENT
  const addParent = () => {
    if (parents.length >= MAX_PARENTS) return;
    setParents((prev) => [
      ...prev,
      {
        name: "",
        email: "",
        phone: "",
        home_number: "",
        work_number: "",
        relationship: "",
      },
    ]);
  };

  // SUBMIT FORM
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (isSubmitting) return;

    // basic validation
    const hasEmpty = parents.some(
      (p) => !p.name || !p.email || !p.phone || !p.relationship
    );

    if (hasEmpty) {
      alert("Please fill all fields.");
      return;
    }

    try {
      setIsSubmitting(true);

      // 1️⃣ CREATE PARENTS
      const createPayload = {
        data: parents,
      };

      const createRes = await makePostRequest(
        `${defaultUrl}/parents/create`,
        createPayload
      );

      // createRes = { parent_id: ["uuid1","uuid2"] }
      const parentIds = createRes?.parent_id ?? [];

      if (!Array.isArray(parentIds) || parentIds.length === 0) {
        console.log("Create response:", createRes);
        throw new Error("No parent_id returned from server.");
      }

      // 2️⃣ LINK PARENTS TO STUDENT
      const linkPayload = {
        data: parentIds.map((id: string) => ({
          parent_id: id,
          children: [student_id],
        })),
      };

      await makePutRequest(`${defaultUrl}/parents/children/add`, linkPayload);

      // SUCCESS
      alert("Parents added & linked successfully!");

      // 3️⃣ RESET FORM
      setParents([
        {
          name: "",
          email: "",
          phone: "",
          home_number: "",
          work_number: "",
          relationship: "",
        },
      ]);

      setIsSubmitting(false);

      // 4️⃣ CLOSE MODAL
      close();
    } catch (error: any) {
      console.error(error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred while saving parent data."
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-xl rounded-2xl p-8 space-y-8 border border-gray-100"
      >
        {/* TITLE */}
        <h2 className="text-2xl font-semibold text-gray-900 text-center mb-2">
          Add & Link Parent / Guardian
        </h2>
        <p className="text-center text-gray-500 text-sm">
          Provide parent/guardian information below. Maximum of 2 entries
          allowed.
        </p>

        {/* PARENT FORMS */}
        {parents.map((parent, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-xl p-6 bg-gray-50 shadow-sm relative"
          >
            {/* HEADER */}
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-semibold text-gray-800">
                Parent / Guardian {index + 1}
              </h3>

              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeParent(index)}
                  className="h-9 w-9 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 shadow"
                  title="Remove parent"
                >
                  <FaTimes size={14} />
                </button>
              )}
            </div>

            {/* GRID FIELDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Name */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={parent.name}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="e.g., John Doe"
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#009688] focus:border-[#009688] transition"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={parent.email}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="example@email.com"
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#009688] focus:border-[#009688] transition"
                />
              </div>

              {/* Phone */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="phone"
                  value={parent.phone}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="08123456789"
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#009688] focus:border-[#009688] transition"
                />
              </div>

              {/* Home Number */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Home Number
                </label>
                <input
                  type="text"
                  name="home_number"
                  value={parent.home_number}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="Home phone"
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#009688] focus:border-[#009688] transition"
                />
              </div>

              {/* Work Number */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Work Number
                </label>
                <input
                  type="text"
                  name="work_number"
                  value={parent.work_number}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="Work phone"
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-[#009688] focus:border-[#009688] transition"
                />
              </div>

              {/* Relationship */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Relationship
                </label>
                <select
                  name="relationship"
                  value={parent.relationship}
                  onChange={(e) => handleChange(index, e)}
                  className="rounded-lg border border-gray-300 px-3 py-2 bg-white focus:ring-2 focus:ring-[#009688] focus:border-[#009688] transition"
                >
                  <option value="">Select...</option>
                  <option>Father</option>
                  <option>Mother</option>
                  <option>Guardian</option>
                  <option>Uncle</option>
                  <option>Aunty</option>
                  <option>Grandparent</option>
                  <option>Sibling</option>
                  <option>Others</option>
                </select>
              </div>
            </div>
          </div>
        ))}

        {/* ADD NEW PARENT BUTTON */}
        {parents.length < 2 && (
          <button
            type="button"
            onClick={addParent}
            className="flex items-center justify-center gap-2 bg-[#009688] hover:bg-[#007f6f] text-white px-4 py-2 rounded-lg shadow transition mx-auto"
          >
            <FaPlus size={14} />
            Add Another Parent
          </button>
        )}

        {/* SUBMIT BUTTON */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-2/3 py-3 rounded-lg text-white font-semibold shadow-md transition
          ${
            isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }
        `}
        >
          {isSubmitting ? "Saving..." : "Save & Link Parent(s)"}
        </button>
      </form>
    </div>
  );
};
